import { AlertCircle, Clock3, Loader2, Reply, Trash, Save } from "lucide-react";
import StepIndicator from "@/shared/components/ui/Status/StepStatus";
import UploadFilesModal from "@/shared/components/ui/Upload/UploadFilesModal";
import { useEffect, useMemo, useState } from "react";
import MarkdownEditor from "@/shared/components/ui/Input/MarkdownEditor";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import { getActionLabel, getChannelIcon } from "@/shared/utils/helperFunctions";
import { getChannelStepsKey } from "@/shared/utils/channelConfig";
import {
  useResetLeadMessage,
  useSaveLeadMessage,
  useUpdateCampaignStepContentUserChanges,
} from "@/features/campaigns/hooks/useCampaign";
import { FixedMessageModal } from "./Form/StepContentModals";
import CampaignMessagesEditor from "./Messaging/CampaignMessagesEditor";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import { renderEmailContent, formatEmailSpacing } from "@/shared/utils/markdownUtils";
import DOMPurify from "dompurify";
import CommonInput from "@/shared/components/ui/Input/CommonInput";
import { Tooltip } from "@heroui/react";
import DeleteConfirmationModal from "@/shared/components/ui/Modal/DeleteConfirmationModal";
import { StatusChip } from "@/shared/components/ui/Status/StatusChip";
import InlineAlert from "@/shared/components/ui/Alert/InlineAlert";
import TextButton from "@/shared/components/ui/Button/TextButton";
import {
  getStaticStepContent,
  getTemplateChannelKey,
  isStaticContentStep,
} from "@/features/campaigns/utils/stepContentMode";
import { proposeEditLearning } from "@/features/campaigns/api/campaignStepsApi";
import { showEditLearningToast } from "@/features/campaigns/components/EditLearningToast";

// A static step is marked on the step row (content_mode) or, for steps that already ran,
// on the timeline row joined with its step (step_content_mode).
const getStaticSource = (step) => {
  if (isStaticContentStep(step)) return step;
  const joined = { ...step?.aiTemplate, channel: step?.channel, action: step?.action };
  return isStaticContentStep(joined) ? joined : null;
};

// The campaign template stands in when AI hasn't written this lead's step yet.
const getCampaignTemplatePreview = (step) =>
  !getStaticSource(step) && step?.previewSource === "campaign" ? step?.staticPreview : null;

// Edited for this lead: the server sends it rendered (staticPreview) with the raw text alongside.
const isLeadEdited = (step) => step?.previewSource === "lead" && Boolean(step?.staticPreview);

// The server sends fixed messages and templates already rendered for this lead (staticPreview).
const getStepBody = (step) => {
  if (isLeadEdited(step)) {
    const text = step.staticPreview.content ?? "";
    return step?.channel === "email" ? formatEmailSpacing(text) : text;
  }
  const staticSource = getStaticSource(step);
  const aiBody = step?.aiTemplate?.content ?? step?.content ?? "";
  const bodyText = staticSource
    ? step?.staticPreview?.content ?? getStaticStepContent(staticSource).content
    : String(aiBody).trim()
      ? aiBody
      : getCampaignTemplatePreview(step)?.content ?? aiBody;
  return step?.channel === "email" ? formatEmailSpacing(bodyText) : bodyText;
};

const getStepSubject = (step) => {
  if (isLeadEdited(step)) return step.staticPreview.subject ?? "";
  const staticSource = getStaticSource(step);
  if (staticSource) return step?.staticPreview?.subject ?? getStaticStepContent(staticSource).subject;
  return (
    step?.aiTemplate?.subject ||
    getCampaignTemplatePreview(step)?.subject ||
    step?.email_subject ||
    step?.subject ||
    ""
  );
};

/**
 * FAILED covers different situations; tell them apart so the card explains what happened
 * instead of showing one generic error icon over an empty editor.
 */
export const getStepFailureKind = (aiTemplate) => {
  if (String(aiTemplate?.status || "").toUpperCase() !== "FAILED") return null;
  if (String(aiTemplate?.content || "").trim()) return "send";
  const meta = aiTemplate?.failure_meta || aiTemplate?.failureMeta || {};
  if (meta.content_not_ready_timeout) return "content_not_ready";
  if (meta.reason === "stale_generating_timeout") return "generation_timeout";
  if (meta.phase === "content_generation") return "generation";
  return "send";
};

const CONTENT_FAILURE_COPY = {
  generation: {
    title: "AI couldn't write this step",
    detail: "It is retried automatically while this lead is open, and again before the step is sent.",
    retryable: true,
  },
  generation_timeout: {
    title: "Writing this step took too long and was stopped",
    detail: "It is retried automatically while this lead is open, and again before the step is sent.",
    retryable: true,
  },
  content_not_ready: {
    title: "This step was not sent",
    detail: "Its content was still missing when it was due to be sent.",
    retryable: false,
  },
};

// User-facing copy keyed by the backend's machine-readable step_skip.code.
// Unknown codes fall back to the reason persisted with the skip.
const SKIP_REASON_COPY = {
  missing_linkedin_profile:
    "This contact does not have a LinkedIn profile. The campaign will continue with the next available step.",
};

const formatGateDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString();
};

const getFailureMessage = (reason, channel, action) => {
  const message = String(reason || "").trim();
  const isLinkedInCompanyPage =
    channel === "linkedin" &&
    /linkedin\.(?:com|cn)\/company(?:[/?#]|$)/i.test(message);

  if (isLinkedInCompanyPage) {
    const isConnectionRequest = ["connect", "linkedin_connect"].includes(
      String(action || "").toLowerCase(),
    );
    return isConnectionRequest
      ? "LinkedIn connection requests cannot be sent to company pages. Use the contact's personal LinkedIn profile URL."
      : "LinkedIn company pages are not supported for this step. Use the contact's personal LinkedIn profile URL.";
  }

  const isConnectionFailure =
    /no active|not connected|disconnected|reconnect|authentication|credential|account.*(?:connection|missing|not found|invalid)|unipile.*account/i.test(
      message,
    );

  if (!isConnectionFailure) {
    return message || "This step failed. No failure reason was recorded.";
  }

  const channelName = {
    linkedin: "LinkedIn",
    whatsapp: "WhatsApp",
    email: "Email",
  }[channel] || "Channel";

  return `${channelName} account connection needs attention. Reconnect the account in Settings, then retry this step.`;
};

const StepsCard = ({ step, stepOrder, onEdit, onDelete, prospectId, enrollmentId, onRetryContent }) => {
  const [allLeadsEditor, setAllLeadsEditor] = useState(null); // "step" | "campaign" | null
  const { mutate: saveLeadMessage, isPending: isSavingLeadMessage } = useSaveLeadMessage();
  const { mutate: resetLeadMessage, isPending: isResettingLeadMessage } = useResetLeadMessage();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [content, setContent] = useState(() => getStepBody(step));
  const [subject, setSubject] = useState(() => getStepSubject(step));
  const { mutate: updateCampaignStepContentUserChanges } =
    useUpdateCampaignStepContentUserChanges();

  const subjectFromStep = getStepSubject(step);
  const bodyFromStep = getStepBody(step);

  useEffect(() => {
    setSubject(subjectFromStep);
    setContent(bodyFromStep);
    setIsEditing(false);
    setUploadModalOpen(false);
  }, [
    prospectId,
    step?.id,
    step?.step_order,
    step?.aiTemplate?.id,
    subjectFromStep,
    bodyFromStep,
  ]);

  const UploadModalClose = () => {
    setUploadModalOpen(false);
  };

  const stepNumber = stepOrder || step?.step_order || 1;
  const stepTitle = getActionLabel(step?.action) || "Initial Outreach";
  const channel = step?.channel || "email";
  const isConnectionRequest =
    channel === "linkedin" &&
    ["connect", "linkedin_connect"].includes(step?.action);
  const stepsChannel = getChannelStepsKey(channel, step?.action);
  const templateText = getStepBody(step);
  const aiTemplate = step?.aiTemplate || {};
  const templateStatus = String(aiTemplate?.status || "").toUpperCase();
  const isFailed = templateStatus === "FAILED";
  const isStaticStep = Boolean(getStaticSource(step));
  const isCampaignTemplate = Boolean(getCampaignTemplatePreview(step)) && !String(aiTemplate?.content ?? "").trim();
  const leadEdited = isLeadEdited(step);
  // Fixed, template and already-edited steps are edited as a lead-only message.
  const editsAsLeadMessage = (isStaticStep || isCampaignTemplate || leadEdited) && Boolean(enrollmentId);
  const rawMessage = step?.staticRaw ?? { subject: subjectFromStep, content: bodyFromStep };
  const failureKind = getStepFailureKind(aiTemplate);
  const contentFailure = !templateText ? CONTENT_FAILURE_COPY[failureKind] : null;
  // Written steps stay visible while the rest of the sequence is generated, so
  // a step that is still being written needs its own state instead of the
  // empty "click to add content" prompt.
  const isWritingContent =
    (templateStatus === "GENERATING" || templateStatus === "PENDING") &&
    !templateText;
  const conditionSkip = aiTemplate?.signals?.condition_skip;
  const stepSkip = aiTemplate?.signals?.step_skip;
  const linkedinGate = aiTemplate?.signals?.linkedin_gate;
  const isSkipped = Boolean(step?.isSkipped || conditionSkip || stepSkip);
  const isWaitingForLinkedInAcceptance =
    channel === "linkedin" && linkedinGate?.status === "waiting_for_acceptance";
  const skipReason =
    SKIP_REASON_COPY[stepSkip?.code] ||
    step?.skipReason ||
    conditionSkip?.reason ||
    stepSkip?.reason ||
    "This step was skipped.";
  const failureMessage = getFailureMessage(
    aiTemplate?.failure_reason || aiTemplate?.failureReason,
    channel,
    step?.action,
  );
  const reply = step?.reply;
  const replyCount = Number(reply?.count || 1);
  const replyDate = reply?.receivedAt ? new Date(reply.receivedAt) : null;
  const replyTimestamp =
    replyDate && !Number.isNaN(replyDate.getTime())
      ? replyDate.toLocaleString()
      : null;
  const replyBody = String(reply?.body || "").trim() || "Reply received";

  const handleStartEdit = () => {
    if (isWritingContent || step?.isReadOnly) return;
    if ((isStaticStep || isCampaignTemplate) && !enrollmentId) return;
    if (isFailed && !editsAsLeadMessage) return;
    onEdit?.(step);
    setIsEditing(true);
    // Lead-only edits start from the raw text so variables and spintax stay editable.
    if (editsAsLeadMessage) {
      setContent(rawMessage.content ?? "");
      setSubject(rawMessage.subject ?? "");
    } else {
      setContent(templateText);
    }
  };

  const editBaseline = editsAsLeadMessage
    ? { content: rawMessage.content ?? "", subject: rawMessage.subject ?? "" }
    : { content: bodyFromStep, subject: subjectFromStep };
  const isSaveEnabled = useMemo(() => {
    return content !== editBaseline.content || subject !== editBaseline.subject;
  }, [content, subject, editBaseline.content, editBaseline.subject]);

  const handleSaveContent = () => {
    if (!isSaveEnabled) return;
    if (editsAsLeadMessage) {
      saveLeadMessage(
        { enrollmentId, stepId: step?.id, subject, content },
        { onSuccess: () => setIsEditing(false) },
      );
      return;
    }
    const payload = {
      content: content,
      subject: subject,
    };
    const templateId = step?.aiTemplate?.id;
    const before = editBaseline;
    updateCampaignStepContentUserChanges(
      { stepId: templateId, payload },
      {
        onSuccess: () => {
          setIsEditing(false);
          // Not tied to this card, so the popup still shows if the user has moved to another page.
          proposeEditLearning({
            templateId,
            originalSubject: before.subject,
            originalContent: before.content,
          })
            .then(showEditLearningToast)
            .catch(() => {});
        },
      },
    );
  };

  return (
    <div className="flex flex-col space-y-6 p-6 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 w-full">
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center">
          {isFailed && (
            <Tooltip
              content={failureMessage || "Execution failed"}
              placement="top"
              className="bg-white dark:bg-gray-800 text-red-600 dark:text-red-400 font-medium text-xs shadow-md border border-gray-200 dark:border-gray-700 px-2.5 py-1 rounded-lg"
            >
              <button
                type="button"
                className="p-1 rounded-md text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors mr-2 cursor-pointer"
              >
                <AlertCircle size={18} />
              </button>
            </Tooltip>
          )}
          <h3 className="px-2 border-r-1 border-gray-200 dark:border-gray-800 text-xs font-semibold text-gray-800 dark:text-gray-100">
            Step {stepNumber}{" "}
          </h3>
          <span className="mx-2">{getChannelIcon({ channel })}</span>
          <h3 className="text-xs font-semibold text-gray-800 dark:text-gray-100">{stepTitle}</h3>
          {isStaticStep && !leadEdited && (
            <StatusChip
              label="Fixed message"
              variant="neutral"
              className="ml-2"
              tooltipContent="Same message for every lead, with their details filled in. Edit it from Campaign Steps."
            />
          )}
          {isCampaignTemplate && !leadEdited && (
            <StatusChip
              label="Campaign template"
              variant="lightBlue"
              className="ml-2"
              tooltipContent="AI hasn't written this lead's message yet, so the campaign template is sent. Click the message to change it for this lead only."
            />
          )}
          {leadEdited && (
            <StatusChip
              label="Edited for this lead"
              variant="warning"
              className="ml-2"
              tooltipContent="Only this lead gets this version. Reset to go back to the shared message."
            />
          )}
          {leadEdited && !isEditing && (
            <TextButton
              className="ml-1"
              isLoading={isResettingLeadMessage}
              loadingText="Resetting…"
              onClick={() => resetLeadMessage({ enrollmentId, stepId: step?.id })}
            >
              Reset
            </TextButton>
          )}
          {!leadEdited && !isEditing && (isStaticStep || isCampaignTemplate) && (
            <TextButton className="ml-1" onClick={() => setAllLeadsEditor(isStaticStep ? "step" : "campaign")}>
              Edit for all leads
            </TextButton>
          )}
        </div>

        {/* step */}
        <div className="flex gap-2 items-center">
          {isSkipped ? (
            <span className="rounded-full bg-amber-100 px-2 py-1 text-[0.65rem] font-semibold text-amber-700">
              Skipped
            </span>
          ) : (
            <StepIndicator data={step} color="brand" channel={stepsChannel} />
          )}
          {step?.isHistorical && (
            <span className="rounded-full bg-gray-100 px-2 py-1 text-[0.65rem] font-semibold text-gray-600">
              Historical
            </span>
          )}
          {reply && (
            <Tooltip
              content={
                <div className="max-w-[320px] text-left">
                  <div className="mb-1 flex items-center justify-between gap-3">
                    <span className="font-semibold">Prospect reply</span>
                    <span className="text-[0.65rem]">
                      {replyCount > 1 ? `${replyCount} replies · latest` : "Latest reply"}
                    </span>
                  </div>
                  <p className="max-h-32 overflow-y-auto whitespace-pre-wrap break-words">
                    {replyBody}
                  </p>
                  {replyTimestamp && (
                    <p className="mt-1 text-[0.65rem]">{replyTimestamp}</p>
                  )}
                </div>
              }
              placement="top"
              className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-xs shadow-md border border-gray-200 dark:border-gray-700 px-2.5 py-2 rounded-lg"
            >
              <button
                type="button"
                className="p-1 rounded-md text-gray-400 hover:text-brand-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                aria-label="View prospect reply"
              >
                <Reply size={16} />
              </button>
            </Tooltip>
          )}
          {!isFailed && !step?.isReadOnly && (
            <Tooltip
              content={"Delete Step"}
              placement="top"
              className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-medium text-xs shadow-md border border-gray-200 dark:border-gray-700 px-2.5 py-1 rounded-lg"
            >
              <button
                type="button"
                onClick={() => setShowDeleteConfirmModal(true)}
                className="p-1 rounded-md text-gray-400 hover:text-red-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <Trash size={16} />
              </button>
            </Tooltip>
          )}
        </div>
      </div>
      {failureKind === "send" && !isSkipped && (
        <InlineAlert variant="danger" title="Sending failed">
          {failureMessage}
        </InlineAlert>
      )}
      {isSkipped && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">{conditionSkip ? "Condition not met" : "Step skipped"}</p>
            <p className="mt-1">{skipReason}</p>
          </div>
        </div>
      )}
      {isWaitingForLinkedInAcceptance && !isSkipped && !isFailed && (
        <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800">
          <Clock3 size={16} className="mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Waiting for LinkedIn connection acceptance</p>
            <p className="mt-1">
              {linkedinGate.reason ||
                "The message will be sent automatically after the lead accepts the connection request."}
            </p>
            {formatGateDate(linkedinGate.next_check_at) && (
              <p className="mt-1 text-[0.65rem] text-blue-700">
                Next check: {formatGateDate(linkedinGate.next_check_at)}
              </p>
            )}
            {formatGateDate(linkedinGate.deadline_at) && (
              <p className="text-[0.65rem] text-blue-700">
                Waiting until: {formatGateDate(linkedinGate.deadline_at)}
              </p>
            )}
          </div>
        </div>
      )}
      {/* content area */}
      {step && !isConnectionRequest && (

        <div className="flex flex-col gap-3">
          {/* subject - only show for email */}
          {channel === "email" &&
            (isEditing || subject || step?.email_subject || step?.subject) && (
              <>
                {isEditing ? (
                  <CommonInput
                    placeholder="Enter subject"
                    type="text"
                    editable={true}
                    className="border border-gray-400 dark:border-gray-700 opacity-80 p-4 rounded-lg text-xs font-bold text-gray-800 dark:text-gray-100"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    required
                  />
                ) : (
                  <div
                    onClick={handleStartEdit}
                    className="border border-[#E9EAEB80] dark:border-gray-800 opacity-80 p-4 rounded-lg text-xs font-bold text-gray-800 dark:text-gray-100 hover:border-gray-300 dark:hover:border-gray-700 transition-colors cursor-pointer"
                  >
                    {subject || (
                      <span className="text-gray-400 dark:text-gray-500 font-normal">
                        Click to enter subject...
                      </span>
                    )}
                  </div>
                )}
              </>
            )}

          {/* template text / email body */}
          {isWritingContent ? (
            <div className="flex items-center gap-2 border border-[#E9EAEB80] dark:border-gray-800 p-4 rounded-lg text-xs text-gray-400 dark:text-gray-500 min-h-[80px]">
              <Loader2 size={14} className="animate-spin shrink-0" />
              <span>Writing content for this step...</span>
            </div>
          ) : contentFailure ? (
            <InlineAlert
              variant="danger"
              title={contentFailure.title}
              className="min-h-[80px]"
              action={
                contentFailure.retryable && onRetryContent ? (
                  <TextButton onClick={onRetryContent}>Retry now</TextButton>
                ) : null
              }
            >
              <p>{contentFailure.detail}</p>
              {failureMessage && <p className="mt-1 text-[0.65rem] opacity-80">Reason: {failureMessage}</p>}
            </InlineAlert>
          ) : isEditing ? (
            <MarkdownEditor
              key={`md-${step?.aiTemplate?.id ?? step?.id}`}
              value={content}
              setValue={setContent}
            />
          ) : (
            <div
              onClick={handleStartEdit}
              className={`border border-[#E9EAEB80] dark:border-gray-800 p-4 rounded-lg text-xs text-gray-800 dark:text-gray-100 opacity-85 min-h-[80px] ${isStaticStep ? "cursor-default" : "cursor-pointer hover:border-gray-300 dark:hover:border-gray-700"} transition-colors max-w-none ${
                channel === "email"
                  ? "markdown-content prose prose-sm dark:prose-invert [&_p]:font-normal [&_strong]:!font-bold [&_b]:!font-bold"
                  : "whitespace-pre-wrap break-words font-sans leading-6"
              }`}
            >
              {templateText ? (
                channel === "email" ? (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        renderEmailContent(templateText),
                      ),
                    }}
                  />
                ) : (
                  templateText
                )
              ) : (
                <span className="text-gray-400">
                  Click to add or edit content (Markdown supported)...
                </span>
              )}
            </div>
          )}

          {/* upload button - only for email */}

          {/* {channel === "email" && (
              <Paperclip
                size={18}
                strokeWidth={2.2}
                className="text-gray-700 dark:text-gray-300"
                className="self-end justify-end flex w-fit cursor-pointer"
                onClick={() => setUploadModalOpen(true)}
              />
            )} */}
          {isEditing && (
            <div className={`flex items-center w-full justify-end`}>
              <div className="flex gap-2">
                <SecondaryButton
                  title="Cancel"
                  onClick={() => setIsEditing(false)}
                  className="shadow-none border-none"
                />
                <PrimaryButton
                  startContent={false}
                  className="self-end justify-end flex w-fit"
                  onClick={handleSaveContent}
                  disabled={!isSaveEnabled || isSavingLeadMessage}
                >
                  <Save size={18} />
                  Save
                </PrimaryButton>
              </div>
            </div>
          )}

          <UploadFilesModal
            uploadModalOpen={uploadModalOpen}
            UploadModalClose={UploadModalClose}
          />
        </div>
      )}

      {allLeadsEditor === "step" && (
        <FixedMessageModal
          isOpen
          onClose={() => setAllLeadsEditor(null)}
          step={{
            id: step?.id,
            channel: step?.channel,
            action: step?.action,
            content_mode: "static",
            template_text: step?.template_text ?? step?.aiTemplate?.step_template_text ?? "",
            email_subject: step?.email_subject ?? step?.aiTemplate?.step_email_subject ?? "",
          }}
        />
      )}
      {allLeadsEditor === "campaign" && (
        <CampaignMessagesEditor
          isOpen
          onClose={() => setAllLeadsEditor(null)}
          initialChannel={getTemplateChannelKey(channel)}
        />
      )}
      {showDeleteConfirmModal && (
        <DeleteConfirmationModal
          title={`Are you sure you want to delete Step ${stepNumber}?`}
          description="Deleting this step will permanently erase its content from the campaign sequence. This action cannot be undone."
          showConfirmDelete={showDeleteConfirmModal}
          onClose={() => setShowDeleteConfirmModal(false)}
          onDelete={() => {
            setShowDeleteConfirmModal(false);
            onDelete?.(step);
          }}
          deleteButtonText="Yes, delete step"
          cancelButtonText="Cancel"
        />
      )}
    </div>
  );
};

export default StepsCard;
